import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { customerAuthOperations, customerProfileOperations, customerReferralOperations } from '../utils/customerSupabase';

const noop = () => {};
const defaultAuthContext = {
  user: null,
  profile: null,
  loading: false,
  error: null,
  isAuthenticated: false,
  isAuthModalOpen: false,
  authModalMode: 'signin',
  signIn: async () => ({ data: null, error: new Error('AuthProvider not mounted') }),
  signOut: async () => ({ error: new Error('AuthProvider not mounted') }),
  resetPassword: async () => ({ data: null, error: new Error('AuthProvider not mounted') }),
  updatePassword: async () => ({ data: null, error: new Error('AuthProvider not mounted') }),
  updateProfile: async () => ({ data: null, error: new Error('AuthProvider not mounted') }),
  refreshProfile: noop,
  openAuthModal: noop,
  closeAuthModal: noop,
  requireAuth: noop,
  verifyEmail: async () => ({ data: null, error: new Error('AuthProvider not mounted') }),
  resendVerification: async () => ({ data: null, error: new Error('AuthProvider not mounted') }),
  clearError: noop
};

const AuthContext = createContext(defaultAuthContext);

export const useAuth = () => {
  const context = useContext(AuthContext);
  return context || defaultAuthContext;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isAuthModalOpen, setIsAuthModalOpen] = useState(false);
  const [authModalMode, setAuthModalMode] = useState('signin');

  // Fetch user profile when user changes
  const fetchProfile = useCallback(async (userId) => {
    // Add timeout to prevent hanging
    const timeoutPromise = new Promise((resolve) => 
      setTimeout(() => {
        console.warn('[Auth] Profile fetch timeout after 3 seconds');
        resolve({ data: null, error: null });
      }, 3000)
    );
    
    try {
      console.log('[Auth] Fetching profile for user:', userId);
      
      const profilePromise = customerProfileOperations.getProfile(userId);
      const { data, error: profileError } = await Promise.race([profilePromise, timeoutPromise]);
      
      if (profileError || !data) {
        console.warn('[Auth] Profile fetch error (may not exist yet):', profileError?.message || 'no profile found');
        setProfile(null);
        return;
      }
      console.log('[Auth] Profile loaded successfully:', data?.full_name || 'No name', { hasProfile: !!data });
      setProfile(data);
    } catch (err) {
      console.error('[Auth] Error fetching profile:', err);
      setProfile(null);
    }
    console.log('[Auth] fetchProfile completed');
  }, []);

  useEffect(() => {
    let mounted = true;
    // Get initial session
    const initializeAuth = async () => {
      try {
        console.log('[Auth] Initializing auth state...');

        const { data: { user: currentUser }, error: authError } = await customerAuthOperations.getCurrentUser();
        
        if (!mounted) {
          return;
        }
        
        // Handle "no session" gracefully - this is expected for anonymous users
        if (authError) {
          console.log('[Auth] Auth error:', authError.message);
          setUser(null);
          setProfile(null);
          setLoading(false);
          return;
        }
        
        if (currentUser) {
          console.log('[Auth] User found:', currentUser.email);
          setUser(currentUser);
          await fetchProfile(currentUser.id);
        } else {
          console.log('[Auth] No user session');
          setUser(null);
          setProfile(null);
        }
        
        setLoading(false);
      } catch (err) {
        if (!mounted) return;
        
        console.log('[Auth] Initialization error:', err.message || err);
        setUser(null);
        setProfile(null);
        setLoading(false);
      }
    };

    initializeAuth();

    // Listen for auth state changes
    const { data: { subscription } } = customerAuthOperations.onAuthStateChange(
      async (event, session) => {
        if (!mounted) return;
        
        console.log('[Auth] Auth state changed:', event);
        
        try {
          if (session?.user) {
            console.log('[Auth] User signed in:', session.user.email);
            setUser(session.user);
            await fetchProfile(session.user.id);
          } else {
            console.log('[Auth] User signed out');
            setUser(null);
            setProfile(null);
          }
        } catch (err) {
          console.error('[Auth] Error in auth state change:', err.message);
        } finally {
          setLoading(false);
        }
      }
    );

    return () => {
      mounted = false;
      subscription?.unsubscribe();
    };
  }, [fetchProfile]);

  // Sign up
  const signUp = async (email, password, fullName) => {
    setLoading(true);
    setError(null);
    
    try {
      const { data, error: signUpError } = await customerAuthOperations.signUp(email, password, fullName);
      
      if (signUpError) throw signUpError;

      if (data?.user?.id && typeof sessionStorage !== 'undefined') {
        const ref = sessionStorage.getItem('referral_code');
        if (ref) {
          try {
            const { data: codeData } = await customerReferralOperations.getByCode(ref);
            if (codeData?.id) await customerReferralOperations.createRedemption(codeData.id, data.user.id);
            sessionStorage.removeItem('referral_code');
          } catch (_) {}
        }
      }
      
      return { data, error: null };
    } catch (err) {
      setError(err.message);
      return { data: null, error: err };
    } finally {
      setLoading(false);
    }
  };

  // Sign in
  const signIn = async (email, password) => {
    setLoading(true);
    setError(null);
    
    try {
      const { data, error: signInError } = await customerAuthOperations.signIn(email, password);
      
      if (signInError) throw signInError;
      
      return { data, error: null };
    } catch (err) {
      setError(err.message);
      return { data: null, error: err };
    } finally {
      setLoading(false);
    }
  };

  // Sign out
  const clearLocalSession = () => {
    if (typeof window === 'undefined') return;
    try {
      Object.keys(localStorage).forEach((key) => {
        if (key.startsWith('sb-') && key.includes('-auth-token')) {
          localStorage.removeItem(key);
        }
        if (key.startsWith('supabase.auth.token')) {
          localStorage.removeItem(key);
        }
      });
    } catch (e) {
      console.warn('[Auth] Failed to clear local session cache', e);
    }
  };

  const signOut = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const { error: signOutError } = await customerAuthOperations.signOut();
      if (signOutError) throw signOutError;
    } catch (err) {
      console.error('[Auth] Sign out error:', err);
      // Proceed with local cleanup even if Supabase call fails
    } finally {
      clearLocalSession();
      setUser(null);
      setProfile(null);
      setLoading(false);
    }
    return { error: null };
  };

  // Reset password
  const resetPassword = async (email) => {
    setLoading(true);
    setError(null);
    
    try {
      const { data, error: resetError } = await customerAuthOperations.resetPassword(email);
      
      if (resetError) throw resetError;
      
      return { data, error: null };
    } catch (err) {
      setError(err.message);
      return { data: null, error: err };
    } finally {
      setLoading(false);
    }
  };

  // Update password
  const updatePassword = async (newPassword) => {
    setLoading(true);
    setError(null);

    try {
      const { data, error: updateError } = await customerAuthOperations.updatePassword(newPassword);

      if (updateError) throw updateError;

      return { data, error: null };
    } catch (err) {
      setError(err.message);
      return { data: null, error: err };
    } finally {
      setLoading(false);
    }
  };

  // Update profile
  const updateProfile = async (profileData) => {
    if (!user) return { error: new Error('Not authenticated') };
    
    try {
      const { data, error: updateError } = await customerProfileOperations.updateProfile(user.id, profileData);
      
      if (updateError) throw updateError;
      
      // Refresh profile
      await fetchProfile(user.id);
      
      return { data, error: null };
    } catch (err) {
      setError(err.message);
      return { data: null, error: err };
    }
  };

  // Refresh profile data
  const refreshProfile = async () => {
    if (user) {
      await fetchProfile(user.id);
    }
  };

  // Open auth modal
  const openAuthModal = (mode = 'signin') => {
    console.log('[Auth] Opening auth modal:', mode);
    setAuthModalMode(mode);
    setIsAuthModalOpen(true);
  };

  // Close auth modal
  const closeAuthModal = () => {
    console.log('[Auth] Closing auth modal');
    setIsAuthModalOpen(false);
  };

  // Require auth - shows modal if not authenticated
  const requireAuth = (callback) => {
    if (user) {
      callback();
    } else {
      console.log('[Auth] Auth required, showing modal');
      openAuthModal('signin');
    }
  };

  const verifyEmail = async (email, code) => {
    try {
      const { data, error } = await customerAuthOperations.verifyEmailOtp(email, code);
      if (error) return { data: null, error };
      // After verification, refresh auth state
      await refreshProfile();
      return { data, error: null };
    } catch (err) {
      return { data: null, error: err };
    }
  };

  const resendVerification = async (email) => {
    try {
      const { data, error } = await customerAuthOperations.resendVerification(email);
      return { data, error };
    } catch (err) {
      return { data: null, error: err };
    }
  };

  const value = {
    user,
    profile,
    loading,
    error,
    isAuthenticated: !!user,
    isAuthModalOpen,
    authModalMode,
    signIn,
    signUp,
    signOut,
    resetPassword,
    updatePassword,
    updateProfile,
    refreshProfile,
    openAuthModal,
    closeAuthModal,
    requireAuth,
    verifyEmail,
    resendVerification,
    clearError: () => setError(null)
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;
